import React, { forwardRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { AlertCircle, Eye, EyeOff, Check } from 'lucide-react';

interface InputProps extends React.InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  error?: string;
  success?: string;
  hint?: string;
  icon?: React.ReactNode;
  iconPosition?: 'left' | 'right';
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      success,
      hint,
      icon,
      iconPosition = 'left',
      className = '',
      type = 'text',
      ...props
    },
    ref
  ) => {
    const [showPassword, setShowPassword] = React.useState(false);
    const isPassword = type === 'password';

    const inputType = isPassword ? (showPassword ? 'text' : 'password') : type;

    const hasError = !!error;
    const hasSuccess = !!success;

    return (
      <div className="w-full">
        {label && (
          <label className="block text-sm font-medium text-text-primary-light dark:text-text-primary-dark mb-1.5">
            {label}
          </label>
        )}

        <div className="relative">
          {icon && iconPosition === 'left' && (
            <div className="absolute left-3 top-1/2 -translate-y-1/2 text-text-secondary-light dark:text-text-secondary-dark">
              {icon}
            </div>
          )}

          <input
            ref={ref}
            type={inputType}
            className={`
              w-full px-4 py-3 rounded-button
              bg-surface-light dark:bg-surface-dark
              border transition-all duration-200
              text-text-primary-light dark:text-text-primary-dark
              placeholder:text-text-secondary-light dark:placeholder:text-text-secondary-dark
              focus:outline-none focus:ring-2 focus:border-transparent
              ${hasError
                ? 'border-error focus:ring-error/50'
                : hasSuccess
                ? 'border-success focus:ring-success/50'
                : 'border-border-light dark:border-border-dark focus:ring-primary/50'
              }
              ${icon && iconPosition === 'left' ? 'pl-10' : ''}
              ${(icon && iconPosition === 'right') || isPassword ? 'pr-10' : ''}
              ${className}
            `}
            {...props}
          />

          {isPassword && (
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary-light dark:text-text-secondary-dark hover:text-text-primary-light dark:hover:text-text-primary-dark transition-colors"
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          )}

          {!isPassword && icon && iconPosition === 'right' && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-text-secondary-light dark:text-text-secondary-dark">
              {icon}
            </div>
          )}

          {hasSuccess && !isPassword && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-success">
              <Check size={18} />
            </div>
          )}
        </div>

        <AnimatePresence mode="wait">
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              className="flex items-center gap-1.5 mt-1.5 text-error text-sm"
            >
              <AlertCircle size={14} />
              {error}
            </motion.div>
          )}
          {success && (
            <motion.div
              initial={{ opacity: 0, y: -4 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -4 }}
              className="flex items-center gap-1.5 mt-1.5 text-success text-sm"
            >
              <Check size={14} />
              {success}
            </motion.div>
          )}
          {hint && !error && !success && (
            <p className="mt-1.5 text-text-secondary-light dark:text-text-secondary-dark text-sm">
              {hint}
            </p>
          )}
        </AnimatePresence>
      </div>
    );
  }
);

Input.displayName = 'Input';

export default Input;
